import React from 'react';
import { Button } from './components/ui/button';
import {
  Users, Dumbbell, Apple, TrendingUp, BarChart3,
  Heart, CheckCircle, DollarSign, Star, BookOpen,
  ArrowRight, Crown, Shield, Percent, Search,
  CreditCard, Upload, Award, Flame
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// HOW IT WORKS
// ═══════════════════════════════════════════════════════════════════════════════
export const HowItWorksPage = ({ onClose }) => {
  const handleGetStarted = () => {
    const redirectUrl = encodeURIComponent(`${window.location.origin}/auth-callback`);
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    window.location.href = `${authUrl}/?redirect=${redirectUrl}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={onClose} className="flex items-center space-x-2.5 hover:opacity-80 transition-opacity">
              <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-9 h-9 rounded-lg" />
              <span className="text-xl font-bold text-[#0f1d3d] tracking-tight">OnlyMass</span>
            </button>
            <Button onClick={onClose} variant="ghost" className="text-slate-600 hover:text-[#0f1d3d] font-medium text-sm gap-1.5">
              <ArrowRight className="h-4 w-4 rotate-180" /> Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-white to-blue-50/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-4">How It Works</p>
          <h1 className="text-4xl lg:text-5xl font-bold leading-tight text-[#0f1d3d] mb-5">
            From Discovery to Results
          </h1>
          <p className="text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto">
            OnlyMass connects you with real fitness creators who share complete programs, structured plans, and expert coaching — not just clips.
          </p>
        </div>
      </section>

      {/* Steps for Fans */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2">For Fans & Subscribers</p>
            <h2 className="text-3xl font-bold text-[#0f1d3d]">Your Path to Better Training</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { num: '01', icon: Search, title: 'Discover Creators', desc: 'Browse by fitness niche — bodybuilding, yoga, HIIT, nutrition, and more. Find creators whose style and expertise match your goals.' },
              { num: '02', icon: Crown, title: 'Subscribe & Unlock', desc: 'Follow for free or subscribe for premium access. Purchase individual workout plans and meal plans from any creator on the platform.' },
              { num: '03', icon: TrendingUp, title: 'Train & Grow', desc: 'Follow structured programs with real exercises, sets, and reps. Track your progress, earn achievements, and see real results.' },
            ].map((step, i) => (
              <div key={i} className="relative p-7 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all group">
                <span className="text-6xl font-bold text-slate-100 absolute top-3 right-5 group-hover:text-blue-100 transition-colors">{step.num}</span>
                <div className="w-11 h-11 bg-[#0f1d3d] rounded-xl flex items-center justify-center mb-5 shadow-sm">
                  <step.icon className="h-5 w-5 text-blue-300" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f1d3d] mb-2">{step.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2">Platform Features</p>
            <h2 className="text-3xl font-bold text-[#0f1d3d]">Everything You Need</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Dumbbell, title: 'Workout Plans', desc: 'Complete programs with exercises, sets, reps, rest periods, and video demonstrations. Buy once or access through subscriptions.', gradient: 'from-blue-600 to-cyan-500' },
              { icon: Apple, title: 'Meal Plans', desc: 'Structured nutrition plans with meals, ingredients, macros, and prep instructions designed by real fitness professionals.', gradient: 'from-emerald-500 to-green-600' },
              { icon: Upload, title: 'Exclusive Posts', desc: 'Premium photos, videos, training tips, and behind-the-scenes content from creators you follow and subscribe to.', gradient: 'from-violet-500 to-purple-600' },
              { icon: Users, title: 'Creator Discovery', desc: 'Find creators by niche, browse curated recommendations, and discover new fitness perspectives across 25+ categories.', gradient: 'from-blue-500 to-indigo-500' },
              { icon: Heart, title: 'Save & Bookmark', desc: 'Build your personal fitness library. Save workout plans, meal plans, and content to access anytime.', gradient: 'from-rose-500 to-pink-500' },
              { icon: Award, title: 'Achievements & Streaks', desc: 'Earn badges, build workout streaks, and track your fitness milestones as you progress through programs.', gradient: 'from-amber-500 to-orange-500' },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all">
                <div className={`w-10 h-10 bg-gradient-to-br ${item.gradient} rounded-lg flex items-center justify-center mb-4 shadow-sm`}>
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-semibold text-[#0f1d3d] mb-1.5">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps for Creators */}
      <section className="py-20 bg-[#0f1d3d]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-2">For Creators</p>
            <h2 className="text-3xl font-bold text-white">Start Earning in Minutes</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { num: '01', icon: Upload, title: 'Create Your Profile', desc: 'Set up your creator page, define your niches, set your subscription price, and showcase your expertise.' },
              { num: '02', icon: BookOpen, title: 'Publish Content', desc: 'Upload workout plans, meal plans, posts, photos, and videos. Set access levels — free, subscriber-only, or one-time purchase.' },
              { num: '03', icon: DollarSign, title: 'Get Paid', desc: 'Earn through subscriptions, plan sales, and tips. Track everything in your analytics dashboard. Payouts via Stripe.' },
            ].map((step, i) => (
              <div key={i} className="relative p-7 rounded-2xl bg-white/[0.06] border border-white/10 hover:border-white/20 transition-colors group">
                <span className="text-6xl font-bold text-white/[0.04] absolute top-3 right-5 group-hover:text-white/[0.08] transition-colors">{step.num}</span>
                <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center mb-5">
                  <step.icon className="h-5 w-5 text-blue-300" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-blue-200/60 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0f1d3d] mb-3">Ready to Start?</h2>
          <p className="text-slate-500 text-lg mb-8">Join OnlyMass today as a fan or creator.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleGetStarted} className="bg-[#0f1d3d] hover:bg-[#1a2d55] text-white font-semibold px-7 py-3 text-base">
              Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button onClick={onClose} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 px-7 py-3 text-base">
              Back to Home
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// FOR CREATORS
// ═══════════════════════════════════════════════════════════════════════════════
export const CreatorsPage = ({ onClose }) => {
  const handleGetStarted = () => {
    const redirectUrl = encodeURIComponent(`${window.location.origin}/auth-callback`);
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    window.location.href = `${authUrl}/?redirect=${redirectUrl}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={onClose} className="flex items-center space-x-2.5 hover:opacity-80 transition-opacity">
              <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-9 h-9 rounded-lg" />
              <span className="text-xl font-bold text-[#0f1d3d] tracking-tight">OnlyMass</span>
            </button>
            <Button onClick={onClose} variant="ghost" className="text-slate-600 hover:text-[#0f1d3d] font-medium text-sm gap-1.5">
              <ArrowRight className="h-4 w-4 rotate-180" /> Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-white to-blue-50/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-4">For Fitness Creators</p>
          <h1 className="text-4xl lg:text-5xl font-bold leading-tight text-[#0f1d3d] mb-5">
            Turn Your Expertise Into Income
          </h1>
          <p className="text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto">
            OnlyMass gives fitness professionals everything they need to build a real business — subscriptions, workout plans, meal plans, analytics, and direct payouts.
          </p>
          <div className="mt-8">
            <Button onClick={handleGetStarted} className="bg-[#0f1d3d] hover:bg-[#1a2d55] text-white font-semibold px-7 py-3 text-base">
              Start as Creator <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* What You Can Do */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2">Creator Tools</p>
            <h2 className="text-3xl font-bold text-[#0f1d3d]">Everything You Need to Earn</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Crown, title: 'Monthly Subscriptions', desc: 'Set your own subscription price. Fans pay monthly for exclusive access to your premium content, posts, and updates.', gradient: 'from-purple-500 to-pink-500' },
              { icon: Dumbbell, title: 'Sell Workout Plans', desc: 'Create structured workout programs with exercises, sets, reps, and media. Sell as one-time purchases or subscriber perks.', gradient: 'from-blue-600 to-cyan-500' },
              { icon: Apple, title: 'Sell Meal Plans', desc: 'Design nutrition plans with detailed meals, macros, ingredients, and instructions. Monetize your nutrition knowledge.', gradient: 'from-emerald-500 to-green-600' },
              { icon: Upload, title: 'Post Content', desc: 'Share photos, videos, training updates, and behind-the-scenes content. Control visibility — public, subscriber-only, or paid.', gradient: 'from-orange-500 to-red-500' },
              { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Track your followers, subscribers, revenue, engagement, profile views, and top-performing content in real time.', gradient: 'from-blue-500 to-indigo-500' },
              { icon: DollarSign, title: 'Direct Payouts', desc: 'Get paid via Stripe Connect. Transparent commission rates, real-time earnings tracking, and reliable payouts.', gradient: 'from-emerald-600 to-teal-500' },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all">
                <div className={`w-10 h-10 bg-gradient-to-br ${item.gradient} rounded-lg flex items-center justify-center mb-4 shadow-sm`}>
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-semibold text-[#0f1d3d] mb-1.5">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revenue */}
      <section className="py-20 bg-[#0f1d3d]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-2">Revenue</p>
            <h2 className="text-3xl font-bold text-white mb-2">Industry-Leading Earnings</h2>
            <p className="text-blue-200/60">Keep more of what you earn. No hidden fees.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-8">
            <div className="text-center p-6 rounded-2xl bg-white/[0.06] border border-white/10">
              <div className="text-4xl font-bold text-white mb-1">80%</div>
              <div className="text-sm font-semibold text-blue-200/80 mb-1">Standard Rate</div>
              <p className="text-xs text-blue-200/50">You keep 80% of all revenue</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-emerald-500/10 border border-emerald-400/20">
              <div className="text-4xl font-bold text-emerald-400 mb-1">85%</div>
              <div className="text-sm font-semibold text-emerald-300 mb-1">Early Adopter</div>
              <p className="text-xs text-blue-200/50">First 3 months for new creators</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-blue-500/10 border border-blue-400/20">
              <div className="text-4xl font-bold text-blue-400 mb-1">85%</div>
              <div className="text-sm font-semibold text-blue-300 mb-1">Founding Creator</div>
              <p className="text-xs text-blue-200/50">Permanent rate for early members</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-blue-200/50">Payouts processed via Stripe Connect. Stripe processing fees apply separately.</p>
          </div>
        </div>
      </section>

      {/* Why OnlyMass for Creators */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2">Why OnlyMass</p>
            <h2 className="text-3xl font-bold text-[#0f1d3d]">Built for Fitness Creators</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              { icon: Shield, title: 'Fitness-Only Platform', desc: 'OnlyMass is exclusively for health and fitness. Your brand stays in a professional, focused environment.' },
              { icon: Star, title: 'Niche Discovery', desc: 'Creators are discoverable by fitness niche. Whether you teach yoga, powerlifting, or meal prep — your audience finds you.' },
              { icon: Flame, title: 'Content Flexibility', desc: 'Post free previews, subscriber-only content, and paid products. Mix revenue streams to maximize your earnings.' },
              { icon: Users, title: 'Audience Ownership', desc: 'Build direct relationships with subscribers. No algorithm deciding your reach. Your fans see your content.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-10 h-10 bg-[#0f1d3d] rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="h-5 w-5 text-blue-300" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#0f1d3d] mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-2xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0f1d3d] mb-3">Ready to Build Your Business?</h2>
          <p className="text-slate-500 text-lg mb-8">Sign up, activate creator mode, and start earning today.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleGetStarted} className="bg-[#0f1d3d] hover:bg-[#1a2d55] text-white font-semibold px-7 py-3 text-base">
              Start as Creator <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button onClick={onClose} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 px-7 py-3 text-base">
              Back to Home
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
